import { Injectable } from '@angular/core';
import { Change, diffLines, LinesOptions } from 'diff';

export type LineDiffType = 'unchanged' | 'added' | 'removed';

export interface AlignedLine {
  type: LineDiffType;
  oldLine: string | null;
  newLine: string | null;
  oldNum: number | null;
  newNum: number | null;
}

export interface JsonDiffSummary {
  addedLines: number;
  removedLines: number;
  unchangedLines: number;
  totalChanges: number;
}

export interface SplitDiffResult {
  lines: AlignedLine[];
  summary: JsonDiffSummary;
  oldJson: string;
  newJson: string;
}

export interface DiffChange {
  count?: number;
  value: string;
  added?: boolean;
  removed?: boolean;
}

export interface DiffResult {
  changes: DiffChange[];
  hasChanges: boolean;
  addedCount: number;
  removedCount: number;
}

export interface FieldDiffResult {
  fieldPath: string;
  fieldLabel: string;
  oldValue: unknown;
  newValue: unknown;
  changeType: 'added' | 'removed' | 'modified' | 'unchanged';
}

export type PropertyDiffType = 'missing' | 'added' | 'unequal' | 'type_changed';

export interface PropertyDiff {
  path: string;
  key: string;
  type: PropertyDiffType;
  oldValue?: unknown;
  newValue?: unknown;
  description: string;
}

export interface JsonDiffResult {
  oldJson: string;
  newJson: string;
  diffs: PropertyDiff[];
  missingCount: number;
  addedCount: number;
  unequalCount: number;
}

export interface FullDiffResult {
  split: SplitDiffResult;
  props: JsonDiffResult;
}

/** Safety cap: abort diff algorithm if edit distance exceeds this. */
const DIFF_OPTIONS: LinesOptions = {
  maxEditLength: 50_000,
} as LinesOptions;

@Injectable({
  providedIn: 'root',
})
export class DiffService {
  /**
   * Pretty-print an object to JSON string without changing key order.
   */
  formatJson(obj: unknown): string {
    if (obj === null || obj === undefined) return '';
    return JSON.stringify(obj, null, 2);
  }

  /**
   * Compute both split-diff and property-diff in one async call.
   * Yields to the event loop between heavy steps so the UI stays responsive.
   */
  async computeFullDiffAsync(
    oldObj: unknown,
    newObj: unknown
  ): Promise<FullDiffResult> {
    // Step 1: stringify (can be slow for huge objects)
    const oldJson = this.formatJson(oldObj);
    const newJson = this.formatJson(newObj);

    // Yield before expensive diffLines
    await this.yieldToEventLoop();

    // Step 2: line diff with safety cap
    const changes = diffLines(oldJson, newJson, DIFF_OPTIONS);

    // Yield before alignment
    await this.yieldToEventLoop();

    // Step 3: align + summarize in one pass
    const lines = this.alignChanges(changes);
    const summary = this.computeSummary(lines);

    // Yield before property diff
    await this.yieldToEventLoop();

    // Step 4: property-level diff
    const diffs: PropertyDiff[] = [];
    this.deepCompare(oldObj, newObj, '', diffs);

    let missingCount = 0;
    let addedCount = 0;
    let unequalCount = 0;
    for (const d of diffs) {
      if (d.type === 'missing') missingCount++;
      else if (d.type === 'added') addedCount++;
      else if (d.type === 'unequal') unequalCount++;
    }

    return {
      split: { lines, summary, oldJson, newJson },
      props: {
        oldJson,
        newJson,
        diffs,
        missingCount,
        addedCount,
        unequalCount,
      },
    };
  }

  /**
   * Build a split diff with aligned lines for side-by-side view.
   */
  splitDiff(oldObj: unknown, newObj: unknown): SplitDiffResult {
    const oldJson = this.formatJson(oldObj);
    const newJson = this.formatJson(newObj);
    const changes = diffLines(oldJson, newJson, DIFF_OPTIONS);
    const lines = this.alignChanges(changes);

    return {
      lines,
      summary: this.computeSummary(lines),
      oldJson,
      newJson,
    };
  }

  private alignChanges(changes: Change[]): AlignedLine[] {
    const estimatedLines = this.estimateLineCount(changes);
    const lines: AlignedLine[] = new Array(estimatedLines);
    let lineIdx = 0;
    const counter = { oldNum: 1, newNum: 1 };
    const skip = new Set<number>();

    for (let i = 0; i < changes.length; i++) {
      if (skip.has(i)) continue;

      const change = changes[i];
      const changeLines = change.value.replace(/\n$/, '').split('\n');

      if (!change.added && !change.removed) {
        lineIdx = this.writeUnchanged(lines, lineIdx, changeLines, counter);
      } else if (change.removed) {
        const next = changes[i + 1];
        if (next?.added) {
          const addedLines = next.value.replace(/\n$/, '').split('\n');
          lineIdx = this.writePaired(
            lines,
            lineIdx,
            changeLines,
            addedLines,
            counter
          );
          skip.add(i + 1);
        } else {
          lineIdx = this.writeRemoved(lines, lineIdx, changeLines, counter);
        }
      } else if (change.added) {
        lineIdx = this.writeAdded(lines, lineIdx, changeLines, counter);
      }
    }

    if (lineIdx < lines.length) {
      lines.length = lineIdx;
    }

    return lines;
  }

  private estimateLineCount(changes: Change[]): number {
    let estimated = 0;
    for (const change of changes) {
      const val = change.value;
      let count = 0;
      for (const ch of val) {
        if (ch.codePointAt(0) === 10) count++;
      }
      estimated +=
        count > 0 && val.codePointAt(val.length - 1) === 10 ? count : count + 1;
    }
    return estimated;
  }

  private writeUnchanged(
    lines: AlignedLine[],
    idx: number,
    changeLines: string[],
    counter: { oldNum: number; newNum: number }
  ): number {
    for (const line of changeLines) {
      lines[idx++] = {
        type: 'unchanged',
        oldLine: line,
        newLine: line,
        oldNum: counter.oldNum++,
        newNum: counter.newNum++,
      };
    }
    return idx;
  }

  private writeRemoved(
    lines: AlignedLine[],
    idx: number,
    changeLines: string[],
    counter: { oldNum: number; newNum: number }
  ): number {
    for (const line of changeLines) {
      lines[idx++] = {
        type: 'removed',
        oldLine: line,
        newLine: null,
        oldNum: counter.oldNum++,
        newNum: null,
      };
    }
    return idx;
  }

  private writeAdded(
    lines: AlignedLine[],
    idx: number,
    changeLines: string[],
    counter: { oldNum: number; newNum: number }
  ): number {
    for (const line of changeLines) {
      lines[idx++] = {
        type: 'added',
        oldLine: null,
        newLine: line,
        oldNum: null,
        newNum: counter.newNum++,
      };
    }
    return idx;
  }

  private writePaired(
    lines: AlignedLine[],
    idx: number,
    removedLines: string[],
    addedLines: string[],
    counter: { oldNum: number; newNum: number }
  ): number {
    const maxLen = Math.max(removedLines.length, addedLines.length);
    for (let j = 0; j < maxLen; j++) {
      const hasOld = j < removedLines.length;
      const hasNew = j < addedLines.length;
      lines[idx++] = {
        type: hasOld ? 'removed' : 'added',
        oldLine: hasOld ? removedLines[j] : null,
        newLine: hasNew ? addedLines[j] : null,
        oldNum: hasOld ? counter.oldNum++ : null,
        newNum: hasNew ? counter.newNum++ : null,
      };
    }
    return idx;
  }

  private computeSummary(lines: AlignedLine[]): JsonDiffSummary {
    let addedLines = 0;
    let removedLines = 0;
    let unchangedLines = 0;
    let totalChanges = 0;

    for (const l of lines) {
      if (l.type === 'unchanged') {
        unchangedLines++;
      } else {
        totalChanges++;
        if (l.type === 'added') {
          addedLines++;
        } else if (l.newLine === null) {
          removedLines++;
        }
      }
    }

    return { addedLines, removedLines, unchangedLines, totalChanges };
  }

  /**
   * Deep-compare two objects and return property-level diffs.
   */
  compareJson(oldObj: unknown, newObj: unknown): JsonDiffResult {
    const oldJson = this.formatJson(oldObj);
    const newJson = this.formatJson(newObj);
    const diffs: PropertyDiff[] = [];
    this.deepCompare(oldObj, newObj, '', diffs);

    let missingCount = 0;
    let addedCount = 0;
    let unequalCount = 0;
    for (const d of diffs) {
      if (d.type === 'missing') missingCount++;
      else if (d.type === 'added') addedCount++;
      else if (d.type === 'unequal') unequalCount++;
    }

    return { oldJson, newJson, diffs, missingCount, addedCount, unequalCount };
  }

  private deepCompare(
    oldObj: unknown,
    newObj: unknown,
    parentPath: string,
    diffs: PropertyDiff[]
  ): void {
    const oldIsObject = typeof oldObj === 'object' && oldObj !== null;
    const newIsObject = typeof newObj === 'object' && newObj !== null;
    if (!oldIsObject && !newIsObject) return;

    const oldRecord = (oldIsObject ? oldObj : {}) as Record<string, unknown>;
    const newRecord = (newIsObject ? newObj : {}) as Record<string, unknown>;
    const allKeys = new Set([
      ...Object.keys(oldRecord),
      ...Object.keys(newRecord),
    ]);

    allKeys.forEach(key => {
      const fullPath = parentPath ? `${parentPath}.${key}` : key;
      const inOld = key in oldRecord;
      const inNew = key in newRecord;

      if (inOld && !inNew) {
        diffs.push({
          path: fullPath,
          key,
          type: 'missing',
          oldValue: oldRecord[key],
          description: `"${key}" removed`,
        });
      } else if (!inOld && inNew) {
        diffs.push({
          path: fullPath,
          key,
          type: 'added',
          newValue: newRecord[key],
          description: `"${key}" added`,
        });
      } else if (inOld && inNew) {
        const oldVal = oldRecord[key];
        const newVal = newRecord[key];
        if (
          typeof oldVal === 'object' &&
          typeof newVal === 'object' &&
          oldVal !== null &&
          newVal !== null &&
          !Array.isArray(oldVal) &&
          !Array.isArray(newVal)
        ) {
          this.deepCompare(oldVal, newVal, fullPath, diffs);
        } else if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
          diffs.push({
            path: fullPath,
            key,
            type: 'unequal',
            oldValue: oldVal,
            newValue: newVal,
            description: `"${key}" changed`,
          });
        }
      }
    });
  }

  diffJson(oldObj: unknown, newObj: unknown): DiffResult {
    const oldStr = this.formatJson(oldObj);
    const newStr = this.formatJson(newObj);
    const changes = diffLines(oldStr, newStr, DIFF_OPTIONS) as DiffChange[];
    const hasChanges = changes.some(c => c.added || c.removed);
    return {
      changes,
      hasChanges,
      addedCount: changes.filter(c => c.added).length,
      removedCount: changes.filter(c => c.removed).length,
    };
  }

  compareFields(
    oldObj: Record<string, unknown> | null | undefined,
    newObj: Record<string, unknown> | null | undefined,
    parentPath = ''
  ): FieldDiffResult[] {
    const safeOld = oldObj ?? {};
    const safeNew = newObj ?? {};
    const results: FieldDiffResult[] = [];
    const allKeys = new Set([...Object.keys(safeOld), ...Object.keys(safeNew)]);
    allKeys.forEach(key => {
      const fullPath = parentPath ? `${parentPath}.${key}` : key;
      const oldVal = safeOld[key];
      const newVal = safeNew[key];
      if (oldVal === undefined && newVal !== undefined) {
        results.push({
          fieldPath: fullPath,
          fieldLabel: key,
          oldValue: undefined,
          newValue: newVal,
          changeType: 'added',
        });
      } else if (newVal === undefined && oldVal !== undefined) {
        results.push({
          fieldPath: fullPath,
          fieldLabel: key,
          oldValue: oldVal,
          newValue: undefined,
          changeType: 'removed',
        });
      } else if (
        typeof oldVal === 'object' &&
        typeof newVal === 'object' &&
        oldVal !== null &&
        newVal !== null &&
        !Array.isArray(oldVal) &&
        !Array.isArray(newVal)
      ) {
        results.push(
          ...this.compareFields(
            oldVal as Record<string, unknown>,
            newVal as Record<string, unknown>,
            fullPath
          )
        );
      } else if (JSON.stringify(oldVal) === JSON.stringify(newVal)) {
        results.push({
          fieldPath: fullPath,
          fieldLabel: key,
          oldValue: oldVal,
          newValue: newVal,
          changeType: 'unchanged',
        });
      } else {
        results.push({
          fieldPath: fullPath,
          fieldLabel: key,
          oldValue: oldVal,
          newValue: newVal,
          changeType: 'modified',
        });
      }
    });
    return results;
  }

  private yieldToEventLoop(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, 0));
  }
}
